import { InterventionStatus } from "../../../components/patient/Patient-Menu/Interventions/data";
import { defaultInterventionSteps, InterventionStepsEnum } from "../../../components/patient/text/data";

// defaultInterventionSteps is sourced from components/patient/text/data.jsx

// Steps that stay closed while the intervention is still being charted.
const postActiveStepNames = new Set([
  InterventionStepsEnum.Documentation,
  InterventionStepsEnum.Billing,
  InterventionStepsEnum.Summary,
]);

// Compute step enablement purely from status
// - Active: enable the charting steps; disable Documentation, Billing and Summary
// - Any other status (WaitingProviderResponse, Completed, Cancelled, etc.): enable only those three
// - Summary is the final wrap-up, so it opens only once Billing has completed
//   the record; it stays closed while a provider response is still pending.
const computeInterventionSteps = (intervention) => {
  const status = intervention?.status;
  const isActive = status === InterventionStatus.Active || !status;
  const isSummaryAvailable = status === InterventionStatus.Completed;
  return defaultInterventionSteps.map((step) => {
    if (step.name === InterventionStepsEnum.Summary) {
      return { ...step, disabled: !isSummaryAvailable };
    }
    const isPostActiveStep = postActiveStepNames.has(step.name);
    return { ...step, disabled: isActive ? isPostActiveStep : !isPostActiveStep };
  });
};

// Backend currently omits the PMR-link fields from intervention assessment / detail
// responses, so an assessment or status update wipes them from the store and the PMR
// "In Review" highlight stops matching. Carry the last known values forward when the
// incoming form omits them entirely (strictly `undefined` — an explicit `false`/`null`
// unlink is respected). Mirrors enrichInterventionSaveResponse / the search merge.
// Remove once the API persists and returns these fields.
const PMR_LINK_DETAIL_FIELDS = [
  "addToPersonalMedicationReview",
  "includeInPersonalMedicationReview",
  "linkedMedicationReviewClinicalServiceId",
];

const preservePmrLinkFields = (incomingForm, prevIntervention) => {
  if (!incomingForm || typeof incomingForm !== "object") return incomingForm;
  const incomingDetails = incomingForm.interventionDetails;
  if (!incomingDetails || typeof incomingDetails !== "object") return incomingForm;
  const prevDetails =
    prevIntervention?.interventionAssessment?.interventionAssessmentForm
      ?.interventionDetails;
  if (!prevDetails) return incomingForm;

  let mergedDetails = incomingDetails;
  for (const key of PMR_LINK_DETAIL_FIELDS) {
    if (incomingDetails[key] === undefined && prevDetails[key] !== undefined) {
      if (mergedDetails === incomingDetails) mergedDetails = { ...incomingDetails };
      mergedDetails[key] = prevDetails[key];
    }
  }
  return mergedDetails === incomingDetails
    ? incomingForm
    : { ...incomingForm, interventionDetails: mergedDetails };
};

const initialState = {
  interventions: [],
  currentIntervention: null,
  clinicalServiceObj: null,
  loading: false,
  error: null,
  fetchByIdFailedForId: null,
  searchFilters: {
    interventionType: null,
    clinicalServiceType: null,
    clinicalServiceTypeCode: null,
    createdDateFrom: null,
    createdDateTo: null
  }
};

const interventionReducer = (state = initialState, action) => {
  switch (action.type) {
    // Create Intervention
    case "CREATE_INTERVENTION_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: [...state.interventions, action.payload],
        currentIntervention: action.payload,
        error: null
      };
    case "CREATE_INTERVENTION_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Update Intervention
    case "UPDATE_INTERVENTION_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: state.interventions.map(intervention =>
          intervention.id === action.payload.id ? action.payload : intervention
        ),
        currentIntervention: action.payload,
        error: null
      };
    case "UPDATE_INTERVENTION_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Fetch Interventions
    case "FETCH_INTERVENTIONS_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: (action.payload || []).map((i) => ({
          ...i,
          interventionSteps: computeInterventionSteps(i)
        })),
        error: null
      };
    case "FETCH_INTERVENTIONS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Fetch Intervention by ID
    case "FETCH_INTERVENTION_BY_ID_SUCCESS": {
      const sameAsCurrent =
        state.currentIntervention?.id === action.payload?.id;
      const incomingForm =
        action.payload?.interventionAssessment?.interventionAssessmentForm;
      const preservedForm = sameAsCurrent
        ? preservePmrLinkFields(incomingForm, state.currentIntervention)
        : incomingForm;
      const nextCurrent = {
        ...action.payload,
        interventionAssessment: {
          ...action.payload?.interventionAssessment,
          interventionAssessmentForm: preservedForm,
        },
      };
      return {
        ...state,
        loading: false,
        fetchByIdFailedForId: null,
        currentIntervention: {
          ...nextCurrent,
          interventionSteps: computeInterventionSteps(nextCurrent),
        },
        error: null,
      };
    }
    case "FETCH_INTERVENTION_BY_ID_FAILURE":
      return {
        ...state,
        loading: false,
        currentIntervention: null,
        fetchByIdFailedForId: action?.payload?.interventionId ?? null,
        error: action?.payload?.errorMessage ?? action.payload ?? null
      };

    // Update Status
    case "UPDATE_INTERVENTION_STATUS_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: state.interventions.map(intervention =>
          intervention.id === action.payload.id
            ? { ...intervention, status: action.payload.status, interventionSteps: computeInterventionSteps({ ...intervention, status: action.payload.status }) }
            : intervention
        ),
        currentIntervention: state.currentIntervention?.id === action.payload.id 
          ? { ...state.currentIntervention, status: action.payload.status, interventionSteps: computeInterventionSteps({ ...state.currentIntervention, status: action.payload.status }) }
          : state.currentIntervention,
        error: null
      };
    case "UPDATE_INTERVENTION_STATUS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Update Assessment
    case "UPDATE_INTERVENTION_ASSESSMENT_SUCCESS": {
      // The API returns only the assessment form data, not the full intervention.
      // We include the interventionId in the payload to identify which intervention to update.
      const { interventionId, ...assessmentData } = action.payload;

      const applyUpdatedForm = (intervention) => {
        const form = preservePmrLinkFields(assessmentData, intervention);
        const next = {
          ...intervention,
          interventionAssessment: {
            ...intervention.interventionAssessment,
            interventionAssessmentForm: form,
          },
        };
        // Recompute steps based on updated assessment (e.g., notification added)
        return { ...next, interventionSteps: computeInterventionSteps(next) };
      };

      return {
        ...state,
        loading: false,
        interventions: state.interventions.map((intervention) =>
          intervention.id === interventionId
            ? applyUpdatedForm(intervention)
            : intervention
        ),
        currentIntervention:
          state.currentIntervention?.id === interventionId
            ? applyUpdatedForm(state.currentIntervention)
            : state.currentIntervention,
        error: null,
      };
    }
    case "UPDATE_INTERVENTION_ASSESSMENT_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Search Filters
    case "SET_SEARCH_FILTERS":
      return {
        ...state,
        searchFilters: { ...state.searchFilters, ...action.payload }
      };
    case "CLEAR_SEARCH_FILTERS":
      return {
        ...state,
        searchFilters: {
          interventionType: null,
          clinicalServiceType: null,
          clinicalServiceTypeCode: null,
          createdDateFrom: null,
          createdDateTo: null
        }
      };

    // General
    case "CLEAR_CURRENT_INTERVENTION":
      return {
        ...state,
        currentIntervention: null,
        fetchByIdFailedForId: null,
        error: null
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload
      };

    // Fetch Clinical Service for Intervention
    case "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_SUCCESS":
      return {
        ...state,
        clinicalServiceObj: action.payload,
        loading: false,
        error: null
      };

    case "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_FAILURE":
      return {
        ...state,
        clinicalServiceObj: null,
        loading: false,
        error: action.payload
      };

    case "RESET_CLINICAL_SERVICE_FOR_INTERVENTION":
      return {
        ...state,
        clinicalServiceObj: null,
        loading: false,
        error: null
      };

    default:
      return state;
  }
};

export default interventionReducer;
